import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  Loader2, 
  Target, 
  FileText, 
  CheckCircle2, 
  ArrowRight, 
  Calendar,
  TrendingUp,
  AlertTriangle,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ValidationHubPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/login?redirect=/hub");
      } else {
        fetchDashboard();
      }
    }
  }, [user, authLoading]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/hub/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const getReadinessConfig = (level) => {
    switch (level) {
      case "RED":
        return { color: "text-red-600", bg: "bg-red-100", label: "Not Ready" };
      case "AMBER":
        return { color: "text-amber-600", bg: "bg-amber-100", label: "Promising" };
      case "GREEN":
        return { color: "text-emerald-600", bg: "bg-emerald-100", label: "Strong" };
      default:
        return { color: "text-gray-600", bg: "bg-gray-100", label: "Unknown" };
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Validation Hub</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.name}! Track your validation progress here.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Link to="/quiz">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full" data-testid="hub-take-quiz">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Take Stress Test</h3>
                  <p className="text-sm text-muted-foreground">Test a new idea</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/canvas">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full" data-testid="hub-create-canvas">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold">Create Canvas</h3>
                  <p className="text-sm text-muted-foreground">Map your idea</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
            <Card className="hover:border-secondary/50 transition-colors cursor-pointer h-full" data-testid="hub-book-call">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Book Call</h3>
                  <p className="text-sm text-muted-foreground">Get expert help</p>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quiz Results */}
            <Card data-testid="hub-quiz-results">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Your Quiz Results
                </CardTitle>
                <CardDescription>Track your idea validation progress</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.quiz_results?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboard.quiz_results.map((quiz, index) => {
                      const config = getReadinessConfig(quiz.readiness_level);
                      return (
                        <Link 
                          key={quiz.id} 
                          to={`/quiz/results/${quiz.id}`}
                          className="block"
                        >
                          <div className="flex items-center justify-between p-4 border rounded-lg hover:border-primary/50 transition-colors" data-testid={`quiz-result-${index}`}>
                            <div className="flex items-center gap-4">
                              <div className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.color}`}>
                                {config.label}
                              </div>
                              <div>
                                <p className="font-medium">Score: {quiz.total_score}/{quiz.max_score}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(quiz.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No quiz results yet</p>
                    <Link to="/quiz">
                      <Button className="rounded-full">Take Your First Test</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Canvases */}
            <Card data-testid="hub-canvases">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-secondary" />
                  Your Canvases
                </CardTitle>
                <CardDescription>View and manage your validation canvases</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.canvases?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboard.canvases.map((canvas, index) => (
                      <Link 
                        key={canvas.id} 
                        to={`/canvas/${canvas.id}`}
                        className="block"
                      >
                        <div className="flex items-center justify-between p-4 border rounded-lg hover:border-secondary/50 transition-colors" data-testid={`canvas-${index}`}>
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-secondary" />
                            </div>
                            <div>
                              <p className="font-medium">{canvas.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(canvas.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No canvases yet</p>
                    <Link to="/canvas">
                      <Button variant="outline" className="rounded-full">Create Your First Canvas</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recommended Next Steps */}
            <Card data-testid="hub-next-steps">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Recommended Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(dashboard?.recommended_steps || []).map((step, index) => (
                    <li key={index} className="flex items-start gap-3" data-testid={`recommended-step-${index}`}>
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{step}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Validation Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Speed beats perfection. Run validation tests within 7 days.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Talk to 5+ potential customers before building anything.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Focus on willingness to pay, not "nice to have" feedback.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
